import React, { useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Snackbar from '@mui/material/Snackbar';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import { useConnection } from '@/app/contexts/ConnectionContext';
import { confirmAsync } from '@/shared/dialogs';
import { ApiService } from '@/utils/api';
import { packageSnapshot } from './packageSnapshot';

const BUCKETS = [
  { value: 'unassigned', label: 'Unassigned' },
  { value: 'review', label: 'Review' },
  { value: 'awaiting_client', label: 'Awaiting client' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'payment', label: 'Awaiting payment' },
] as const;

type Bucket = (typeof BUCKETS)[number]['value'];

interface AgentTaskMessage {
  id: string;
  visibility: string;
  kind: string;
  body: string;
  delivery?: string;
  snapshot?: boolean;
}

interface AgentTask {
  id: string;
  workflow_state: string;
  payment_status: string;
  client_object: string;
  client_id: string;
  agent_node_id: string;
  telegram_chat_id: string;
  brief: string;
  package_scope: string;
  package_time: string;
  package_cost: string;
  payment_record_id: string;
  messages: AgentTaskMessage[];
  turn_in_flight?: boolean;
  panel_error?: string;
}

const POST_ACTIONS: Array<{ label: string; path: string; body?: Record<string, string> }> = [
  { label: 'Accept plan', path: 'accept-plan' },
  { label: 'Reject plan', path: 'reject-plan' },
  { label: 'Return package', path: 'return-package' },
  { label: 'Client accepted', path: 'accept-client' },
  { label: 'Client declined', path: 'decline-client' },
  { label: 'Done', path: 'done' },
  { label: 'Mark awaiting', path: 'payment', body: { status: 'awaiting' } },
  { label: 'Mark paid', path: 'payment', body: { status: 'paid' } },
];

function normalizeTask(raw: AgentTask): AgentTask {
  return {
    ...raw,
    messages: raw.messages ?? [],
    turn_in_flight: raw.turn_in_flight === true,
  };
}

function panelErrorLabel(code: string | undefined): string | null {
  if (code === 'not_received') return 'Reply not received';
  if (code === 'rejected') return 'Reply rejected';
  return null;
}

function latestSnapshotDelivery(task: AgentTask): string | undefined {
  let delivery: string | undefined;
  for (const message of task.messages) {
    if (message.snapshot === true) delivery = message.delivery;
  }
  return delivery;
}

export function AgentTaskDesk() {
  const { selectedConnection } = useConnection();
  const connectionId = selectedConnection?.id ?? null;
  const [bucket, setBucket] = useState<Bucket>('unassigned');
  const [tasks, setTasks] = useState<AgentTask[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [clientId, setClientId] = useState('');
  const [reply, setReply] = useState('');
  const [paymentRecordId, setPaymentRecordId] = useState('');
  const [notice, setNotice] = useState<string | null>(null);
  const pendingRef = useRef(false);
  const acceptPackageRef = useRef<HTMLButtonElement>(null);
  const returnFocusRef = useRef(false);

  useEffect(() => {
    if (!connectionId) {
      return undefined;
    }
    let active = true;
    void ApiService.get<AgentTask[]>(`/v1/app/${connectionId}/agent-tasks?bucket=${bucket}`)
      .then((rows) => {
        if (!active) return;
        setTasks(Array.isArray(rows) ? rows.map(normalizeTask) : []);
      })
      .catch(() => {
        if (!active) return;
        setTasks([]);
        setNotice('Could not load tasks');
      });
    return () => {
      active = false;
    };
  }, [connectionId, bucket]);

  useEffect(() => {
    if (!pending && returnFocusRef.current) {
      returnFocusRef.current = false;
      acceptPackageRef.current?.focus();
    }
  }, [pending]);

  const selected = tasks.find((item) => item.id === selectedId) ?? tasks[0] ?? null;
  const actionsDisabled = pending || selected?.turn_in_flight === true;
  const panelError = selected ? panelErrorLabel(selected.panel_error) : null;

  async function runMutation(path: string, body?: Record<string, string>, announceSnapshot = false) {
    if (!connectionId || !selected || pendingRef.current || selected.turn_in_flight === true) {
      return;
    }
    pendingRef.current = true;
    setPending(true);
    try {
      const url = `/v1/app/${connectionId}/agent-tasks/${selected.id}/${path}`;
      const returned =
        body === undefined
          ? await ApiService.post<AgentTask>(url)
          : await ApiService.post<AgentTask>(url, body);
      if (returned && typeof returned === 'object' && returned.id) {
        const next = normalizeTask(returned);
        setTasks((current) => current.map((item) => (item.id === next.id ? next : item)));
        if (announceSnapshot) {
          setNotice(latestSnapshotDelivery(next) === 'delivered' ? 'Sent' : null);
        } else {
          setNotice(null);
        }
      }
    } catch {
      setNotice('Request failed');
    } finally {
      pendingRef.current = false;
      setPending(false);
    }
  }

  async function onAcceptPackage() {
    if (!connectionId || !selected || actionsDisabled) return;
    const text = packageSnapshot(selected.package_scope, selected.package_time, selected.package_cost);
    let accepted = false;
    try {
      accepted = await confirmAsync(text);
    } finally {
      acceptPackageRef.current?.focus();
    }
    if (!accepted) return;
    returnFocusRef.current = true;
    await runMutation('confirm-package', { text }, true);
  }

  function onCopyBrief() {
    if (!selected || actionsDisabled) return;
    try {
      void navigator.clipboard.writeText(selected.brief);
    } catch {
      // Clipboard is missing outside a secure browser context.
    }
  }

  if (!connectionId) {
    return <Box />;
  }

  return (
    <Box display="flex" flexDirection="column" gap={1} p={1}>
      <ToggleButtonGroup
        exclusive
        size="small"
        value={bucket}
        aria-label="Task bucket"
        onChange={(_event, next: Bucket | null) => {
          if (next) setBucket(next);
        }}
        sx={{ flexWrap: 'wrap' }}
      >
        {BUCKETS.map((item) => (
          <ToggleButton key={item.value} value={item.value}>
            {item.label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>

      {selected ? (
        <>
          <Box display="flex" flexWrap="wrap" gap={0.5}>
            {tasks.map((item) => (
              <Button
                key={item.id}
                size="small"
                variant={item.id === selected.id ? 'contained' : 'text'}
                onClick={() => setSelectedId(item.id)}
              >
                {item.brief || item.id}
              </Button>
            ))}
          </Box>

          <Box>
            {selected.messages.map((message) => {
              const operator = message.visibility === 'operator';
              return (
                <Box key={message.id} sx={{ mb: 1 }}>
                  {operator ? (
                    <Typography variant="caption" color="text.secondary" display="block">
                      Operator
                    </Typography>
                  ) : null}
                  <Typography
                    variant="body2"
                    color={operator ? 'text.secondary' : 'text.primary'}
                    sx={{ whiteSpace: 'pre-wrap' }}
                  >
                    {message.body}
                  </Typography>
                  {message.delivery === 'undelivered' ? (
                    <>
                      <Typography variant="caption" color="text.secondary" display="block">
                        undelivered
                      </Typography>
                      <Button
                        size="small"
                        disabled={actionsDisabled}
                        onClick={() => void runMutation('retry', { message_id: message.id })}
                      >
                        Retry
                      </Button>
                    </>
                  ) : null}
                </Box>
              );
            })}
          </Box>

          {panelError ? (
            <Typography variant="body2" color="text.secondary">
              {panelError}
            </Typography>
          ) : null}

          <TextField
            size="small"
            label="Client id"
            value={clientId}
            onChange={(event) => setClientId(event.target.value)}
          />
          <Button size="small" disabled={actionsDisabled} onClick={() => void runMutation('assign', { client_id: clientId })}>
            Assign
          </Button>

          <TextField
            size="small"
            label="Reply"
            value={reply}
            onChange={(event) => setReply(event.target.value)}
          />
          <Button size="small" disabled={actionsDisabled} onClick={() => void runMutation('reply', { text: reply })}>
            Reply
          </Button>

          {POST_ACTIONS.map((action) => (
            <Button
              key={action.label}
              size="small"
              disabled={actionsDisabled}
              onClick={() => void runMutation(action.path, action.body)}
            >
              {action.label}
            </Button>
          ))}

          <TextField
            size="small"
            label="Payment record"
            value={paymentRecordId}
            onChange={(event) => setPaymentRecordId(event.target.value)}
          />
          <Button
            size="small"
            disabled={actionsDisabled}
            onClick={() => void runMutation('payment-link', { record_id: paymentRecordId })}
          >
            Link payment
          </Button>

          <Button size="small" disabled={actionsDisabled} onClick={onCopyBrief}>
            Copy brief
          </Button>
          <Button
            ref={acceptPackageRef}
            size="small"
            disabled={actionsDisabled}
            onClick={() => void onAcceptPackage()}
          >
            Accept package
          </Button>
        </>
      ) : null}

      <Snackbar open={notice !== null} message={notice ?? ''} onClose={() => setNotice(null)} />
    </Box>
  );
}
